import React, { useMemo } from 'react'
import * as S from './MeasurementLine.styles'
import { Line, Orientation } from '../types'
import {
  useGetMapPointToCanvasRelativePosition,
  useGetMapSizeToCanvasRelativeSize,
} from '../../../hooks/inspector'
import { roundWithLocale } from '@sketch/utils'

export type MeasurementLineProps = {
  line: Line
  orientation: Orientation
}

export function MeasurementLine({ line, orientation }: MeasurementLineProps) {
  const relativeLine = useMapLineValuesToBeRelativeToCanvas(line)

  if (!relativeLine || line.size < 1) {
    return null
  }

  const distanceToDisplay = roundWithLocale(line.size, 2)

  return (
    <S.MeasurementLine
      $x={relativeLine.x}
      $y={relativeLine.y}
      $orientation={orientation}
      $lineSize={relativeLine?.size}
    >
      <S.LineEndMark $orientation={orientation} $side="start" />

      <S.MeasurementPill $orientation={orientation}>
        {distanceToDisplay}
      </S.MeasurementPill>
      <S.LineEndMark $orientation={orientation} $side="end" />
    </S.MeasurementLine>
  )
}
interface MeasurementLineProjectionProps {
  line: Line
  orientation: Orientation
}

export function MeasurementLineProjection({
  line,
  orientation,
}: MeasurementLineProjectionProps) {
  const relativeLine = useMapLineValuesToBeRelativeToCanvas(line)

  if (!relativeLine || line.size < 1) {
    return null
  }

  return (
    <S.MeasurementLineProjection
      data-testid="measurement-line-projection"
      $x={relativeLine.x}
      $y={relativeLine.y}
      $orientation={orientation}
      $lineSize={relativeLine?.size}
    />
  )
}

function useMapLineValuesToBeRelativeToCanvas(line: Line) {
  const mapPointToCanvasRelativePosition =
    useGetMapPointToCanvasRelativePosition()

  const mapSizeToCanvasRelativeSize = useGetMapSizeToCanvasRelativeSize()

  const relativeLine = useMemo((): Line | null => {
    const relPosition = mapPointToCanvasRelativePosition({
      x: line.x,
      y: line.y,
    })

    const relativeSize = mapSizeToCanvasRelativeSize(line.size)

    if (!relPosition || !relativeSize) {
      return null
    }

    return {
      x: relPosition.x,
      y: relPosition.y,
      size: relativeSize,
    }
  }, [
    mapPointToCanvasRelativePosition,
    line.x,
    line.y,
    line.size,
    mapSizeToCanvasRelativeSize,
  ])

  return relativeLine
}
